import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  Switch,
  SimpleGrid,
  Divider,
  Text,
  Flex,
  useDisclosure,
  IconButton,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import {
  getAllUsers,
  createCustomerUser,
  getAllOrganizations,
  deleteUser,
  updateUserAdmin,
  sendPasswordResetEmail,
  setUserDefaultRole,
} from "../../services/adminService";
import { getOrgRoleConfig } from "../../services/organizationService";
import { useAdminData } from "../../hooks/useAdminData";
import { createToast } from "../../utils/toastUtils";
import { usePersistentState } from "../../hooks/usePersistentState";
import ReportingGroupManagement from "./ReportingGroupManagement";

const UserManagement = () => {
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingAdminId, setUpdatingAdminId] = useState(null);
  const [updatingDefaultRoleId, setUpdatingDefaultRoleId] = useState(null);
  const [roleConfigByOrg, setRoleConfigByOrg] = useState({});
  const [resettingId, setResettingId] = useState(null);
  const [filterOrgId, setFilterOrgId] = usePersistentState(
    "admin:userManagement:filterOrgId",
    ""
  );
  const [newUser, setNewUser] = useState({
    email: "",
    first_name: "",
    last_name: "",
    organization_id: "",
    role: "employee",
    is_admin: false,
    password: "",
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const toastHelper = createToast(toast);

  // Memoized fetch functions to prevent unnecessary re-renders
  const fetchUsersWithFilter = useCallback(() => {
    return getAllUsers(filterOrgId || null);
  }, [filterOrgId]);

  const fetchOrganizations = useCallback(() => {
    return getAllOrganizations();
  }, []);

  // Use consolidated admin data hooks
  const {
    data: usersData,
    loading: usersLoading,
    error: usersError,
    refetch: fetchUsers,
  } = useAdminData(fetchUsersWithFilter, [filterOrgId]);

  const {
    data: organizationsData,
    loading: orgsLoading,
    error: orgsError,
  } = useAdminData(fetchOrganizations, []);

  const users = usersData?.users || [];
  const organizations = organizationsData?.organizations || [];
  const loading = usersLoading || orgsLoading;
  const error = usersError || orgsError;

  useEffect(() => {
    let cancelled = false;
    const loadRoleConfigs = async () => {
      const orgList = organizationsData?.organizations || [];
      if (!orgList.length) {
        setRoleConfigByOrg({});
        return;
      }
      const entries = await Promise.all(
        orgList.map(async (org) => {
          try {
            const data = await getOrgRoleConfig(org.id);
            return [org.id, data.role_config || {}];
          } catch (err) {
            console.warn("Unable to load role config", org.id, err);
            return [org.id, {}];
          }
        })
      );
      if (!cancelled) {
        setRoleConfigByOrg(Object.fromEntries(entries));
      }
    };
    loadRoleConfigs();
    return () => {
      cancelled = true;
    };
  }, [organizationsData]);

  const roleOptionsByOrg = useMemo(() => {
    const out = {};
    Object.entries(roleConfigByOrg).forEach(([orgId, roleConfig]) => {
      const options = [];
      Object.entries(roleConfig?.roles || roleConfig || {}).forEach(([roleId, versions]) => {
        Object.entries(versions || {}).forEach(([version, config]) => {
          const labelParts = [
            roleId,
            `v${version}`,
            config?.coaching_type,
          ].filter(Boolean);
          options.push({
            roleId,
            version: Number(version),
            label: labelParts.join(" · "),
          });
        });
      });
      out[orgId] = options.sort((a, b) => a.label.localeCompare(b.label));
    });
    return out;
  }, [roleConfigByOrg]);

  const handleCreateUser = async () => {
    if (
      !newUser.email.trim() ||
      !newUser.first_name.trim() ||
      !newUser.last_name.trim() ||
      !newUser.organization_id
    ) {
      toastHelper.error(
        "Error",
        "Email, first name, last name, and organization are required"
      );
      return;
    }

    setCreating(true);
    try {
      const payload = {
        email: newUser.email.trim(),
        first_name: newUser.first_name.trim(),
        last_name: newUser.last_name.trim(),
        organization_id: parseInt(newUser.organization_id),
        role: newUser.role,
        is_admin: newUser.is_admin,
      };
      if (newUser.password) {
        payload.password = newUser.password;
      }

      const created = await createCustomerUser(payload);

      const userEmail = created?.user?.email ?? newUser.email.trim();
      const hasPassword = !!newUser.password;
      toastHelper.success(
        "User created",
        hasPassword
          ? `${userEmail} can now log in immediately`
          : `Verification email sent to ${userEmail}`
      );

      setNewUser({
        email: "",
        first_name: "",
        last_name: "",
        organization_id: "",
        role: "employee",
        is_admin: false,
        password: "",
      });
      onClose();
      fetchUsers(); // Refresh the list
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        toastHelper.error(
          "Password rejected",
          "Password doesn't meet security requirements"
        );
      } else if (status === 409) {
        toastHelper.error("Email already exists", err.response?.data?.detail || "A user with this email already exists");
      } else if (status === 502) {
        toastHelper.error(
          "Authentication server unavailable",
          "Could not reach authentication server, try again"
        );
      } else {
        toastHelper.error(
          "Error",
          err.response?.data?.detail || "Failed to create user"
        );
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    setDeletingId(userId);
    try {
      await deleteUser(userId);
      toastHelper.success("User deleted", "User has been removed");
      fetchUsers();
    } catch (err) {
      toastHelper.error(
        "Error",
        err.response?.data?.detail || "Failed to delete user"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleAdmin = async (user) => {
    setUpdatingAdminId(user.id);
    try {
      await updateUserAdmin(user.id, { is_admin: !user.is_admin });
      toastHelper.success(
        "User updated",
        `${user.email} is now ${user.is_admin ? "not an admin" : "an admin"}`
      );
      fetchUsers();
    } catch (err) {
      toastHelper.error(
        "Error",
        err.response?.data?.detail || "Failed to update admin status"
      );
    } finally {
      setUpdatingAdminId(null);
    }
  };

  const handleSendReset = async (user) => {
    setResettingId(user.id);
    try {
      await sendPasswordResetEmail(user.id);
      toastHelper.success("Reset email sent", `Password reset link sent to ${user.email}`);
    } catch (err) {
      toastHelper.error(
        "Error",
        err.response?.data?.detail || "Failed to send reset email"
      );
    } finally {
      setResettingId(null);
    }
  };

  const handleSetDefaultRole = async (user, value) => {
    const payload = value
      ? (() => {
          const [roleId, version] = value.split(":");
          return {
            default_role_id: roleId,
            default_role_version: Number(version) || 1,
          };
        })()
      : { default_role_id: null, default_role_version: null };

    setUpdatingDefaultRoleId(user.id);
    try {
      await setUserDefaultRole(user.id, payload);
      toastHelper.success("Default role updated", user.email);
      fetchUsers();
    } catch (err) {
      toastHelper.error(
        "Error",
        err.response?.data?.detail || "Failed to update default role"
      );
    } finally {
      setUpdatingDefaultRoleId(null);
    }
  };

  const getOrganizationName = (orgId) => {
    const org = organizations.find((o) => o.id === orgId);
    return org ? org.name : `Unknown (${orgId})`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <VStack spacing={3}>
          <Box
            w={8} h={8} borderRadius="full" borderWidth="3px"
            borderColor="blue.500" borderTopColor="transparent"
            animation="spin 0.8s linear infinite"
          />
          <Text fontSize="sm" color="gray.500">Loading users...</Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <VStack spacing={5} align="stretch">
      {/* Toolbar */}
      <Flex justify="space-between" align="flex-end" gap={4} wrap="wrap">
        <FormControl maxW="280px">
          <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
            Organization
          </FormLabel>
          <Select
            size="sm"
            value={filterOrgId}
            onChange={(e) => setFilterOrgId(e.target.value)}
            placeholder="All organizations"
            borderRadius="md"
            bg="white"
          >
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <Button
          colorScheme="blue"
          size="sm"
          px={5}
          borderRadius="md"
          fontWeight="semibold"
          onClick={onOpen}
        >
          + Create User
        </Button>
      </Flex>

      {/* Table */}
      {users.length === 0 ? (
        <Box
          py={16}
          textAlign="center"
          bg="gray.50"
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          borderStyle="dashed"
        >
          <Text color="gray.400" fontSize="sm">No users found</Text>
        </Box>
      ) : (
        <Box
          overflowX="auto"
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          bg="white"
        >
          <Table variant="unstyled" size="sm">
            <Thead>
              <Tr bg="gray.50" borderBottomWidth="1px" borderBottomColor="gray.200">
                <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">#</Th>
                <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">Email</Th>
                <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">Organization</Th>
                <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">Status</Th>
                <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">Role</Th>
                <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">Default Coaching Role</Th>
                <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">Admin</Th>
                <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">Keycloak</Th>
                <Th py={3} px={4} />
              </Tr>
            </Thead>
            <Tbody>
              {users.map((user, idx) => (
                <Tr
                  key={user.id}
                  borderBottomWidth={idx < users.length - 1 ? "1px" : "0"}
                  borderBottomColor="gray.100"
                  _hover={{ bg: "gray.50" }}
                  transition="background 0.1s"
                >
                  <Td py={3} px={4}>
                    <Text fontSize="xs" color="gray.400" fontFamily="mono">{user.id}</Text>
                  </Td>
                  <Td py={3} px={4}>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="semibold">{user.email}</Text>
                      {(user.first_name || user.last_name) && (
                        <Text fontSize="xs" color="gray.500">
                          {[user.first_name, user.last_name].filter(Boolean).join(" ")}
                        </Text>
                      )}
                    </VStack>
                  </Td>
                  <Td py={3} px={4}>
                    <Text fontSize="sm" color="gray.700">{getOrganizationName(user.organization_id)}</Text>
                  </Td>
                  <Td py={3} px={4}>
                    {user.is_active ? (
                      <Badge colorScheme="green" variant="subtle" borderRadius="full" px={2} fontSize="xs">Active</Badge>
                    ) : (
                      <Badge colorScheme="yellow" variant="subtle" borderRadius="full" px={2} fontSize="xs">Pending</Badge>
                    )}
                  </Td>
                  <Td py={3} px={4}>
                    {user.role ? (
                      <Badge colorScheme="gray" variant="subtle" borderRadius="full" px={2} fontSize="xs" textTransform="capitalize">
                        {user.role}
                      </Badge>
                    ) : (
                      <Text fontSize="xs" color="gray.400">—</Text>
                    )}
                  </Td>
                  <Td py={3} px={4} minW="240px">
                    <Select
                      size="sm"
                      value={
                        user.default_role_id
                          ? `${user.default_role_id}:${user.default_role_version || 1}`
                          : ""
                      }
                      placeholder="No default (notetaking)"
                      isDisabled={updatingDefaultRoleId === user.id}
                      onChange={(e) => handleSetDefaultRole(user, e.target.value)}
                      bg="white"
                      borderRadius="md"
                    >
                      {(roleOptionsByOrg[user.organization_id] || []).map((option) => (
                        <option
                          key={`${option.roleId}:${option.version}`}
                          value={`${option.roleId}:${option.version}`}
                        >
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                  <Td py={3} px={4}>
                    <HStack spacing={2}>
                      <Switch
                        size="sm"
                        isChecked={!!user.is_admin}
                        isDisabled={updatingAdminId === user.id}
                        onChange={() => handleToggleAdmin(user)}
                      />
                      <Text fontSize="xs" color={user.is_admin ? "purple.600" : "gray.500"}>
                        {user.is_admin ? "Admin" : "User"}
                      </Text>
                    </HStack>
                  </Td>
                  <Td py={3} px={4}>
                    <Badge
                      colorScheme={user.keycloak_uuid ? "green" : "gray"}
                      variant="subtle"
                      borderRadius="full"
                      px={2}
                      fontSize="xs"
                    >
                      {user.keycloak_uuid ? "Linked" : "Unlinked"}
                    </Badge>
                  </Td>
                  <Td py={3} px={4} textAlign="right">
                    <HStack spacing={1} justify="flex-end">
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        isLoading={resettingId === user.id}
                        isDisabled={!user.keycloak_uuid}
                        onClick={() => handleSendReset(user)}
                      >
                        Reset Password
                      </Button>
                      <IconButton
                        aria-label="Delete user"
                        icon={<DeleteIcon />}
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        borderRadius="md"
                        isLoading={deletingId === user.id}
                        onClick={() => handleDeleteUser(user.id)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {filterOrgId && (
        <ReportingGroupManagement
          organizationId={Number(filterOrgId)}
          organizations={organizations}
          onOrganizationChange={(organizationId) => setFilterOrgId(String(organizationId))}
          users={users}
        />
      )}

      {/* Create User Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" boxShadow="xl">
          <ModalHeader
            pb={3}
            pt={6}
            px={6}
            fontSize="lg"
            fontWeight="semibold"
            borderBottomWidth="1px"
            borderBottomColor="gray.100"
          >
            Create New User
          </ModalHeader>
          <ModalCloseButton top={4} right={4} />
          <ModalBody px={6} py={5}>
            <VStack spacing={5}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Email</FormLabel>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                  placeholder="john@acme.com"
                />
              </FormControl>

              <SimpleGrid columns={2} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm">First Name</FormLabel>
                  <Input
                    value={newUser.first_name}
                    onChange={(e) =>
                      setNewUser({ ...newUser, first_name: e.target.value })
                    }
                    placeholder="John"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Last Name</FormLabel>
                  <Input
                    value={newUser.last_name}
                    onChange={(e) =>
                      setNewUser({ ...newUser, last_name: e.target.value })
                    }
                    placeholder="Doe"
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Organization</FormLabel>
                <Select
                  value={newUser.organization_id}
                  onChange={(e) =>
                    setNewUser({ ...newUser, organization_id: e.target.value })
                  }
                  placeholder="Select organization"
                >
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <SimpleGrid columns={2} spacing={4} w="full" alignItems="end">
                <FormControl>
                  <FormLabel fontSize="sm">Role</FormLabel>
                  <Select
                    value={newUser.role}
                    onChange={(e) =>
                      setNewUser({ ...newUser, role: e.target.value })
                    }
                  >
                    <option value="employee">Employee</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={3}>Admin User</FormLabel>
                  <HStack>
                    <Switch
                      id="is-admin"
                      isChecked={newUser.is_admin}
                      onChange={(e) =>
                        setNewUser({ ...newUser, is_admin: e.target.checked })
                      }
                    />
                    <Text fontSize="sm" color={newUser.is_admin ? "blue.500" : "gray.500"}>
                      {newUser.is_admin ? "Yes" : "No"}
                    </Text>
                  </HStack>
                </FormControl>
              </SimpleGrid>

              <Divider />

              <FormControl>
                <FormLabel fontSize="sm">
                  Password{" "}
                  <Text as="span" fontWeight="normal" color="gray.500">
                    (optional)
                  </Text>
                </FormLabel>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) =>
                    setNewUser({ ...newUser, password: e.target.value })
                  }
                  placeholder="Leave blank to send setup email"
                />
                <FormHelperText color={newUser.password ? "green.500" : "gray.500"}>
                  {newUser.password
                    ? "User will be able to log in immediately"
                    : "A setup email will be sent to the user"}
                </FormHelperText>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter
            px={6}
            py={4}
            borderTopWidth="1px"
            borderTopColor="gray.100"
            gap={2}
          >
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              size="sm"
              px={6}
              onClick={handleCreateUser}
              isLoading={creating}
              loadingText="Creating..."
            >
              Create User
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default UserManagement;
